import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Switch,
  Route,
  Redirect,
} from "react-router-dom";
import BranchDashboard from "./layouts/branchDashboard";
import AdminDashboard from "./layouts/adminDashboard";
import BranchSelector from "./features/user/components/BranchSelector";
import SignIn from "./features/user/SignIn";
import SignOut from "./features/user/SignOut";
import Forgot from "./features/user/Forgot";
import Verify from "./features/user/Verify";
import ChangePassword from "./features/registration/components/ChangePassword";
import LinkProvider from "./features/registration/components/LinkProvider";
import Unauthorized from "./Unauthorized";
import { useSelector } from "react-redux";
import { useProfile } from "./hooks";
import {
  useOrganizationPermission,
  useUserBranches,
  useRoutePermission,
} from "./hooks/accessControl";
import Auth from "./api/auth";

const RestrictedRoute = ({ location, children, ...rest }) => {
  const hasAccess = useRoutePermission(location.pathname);
  if (!hasAccess) {
    return <Redirect to="/unauthorized" />;
  }
  return (
    <Route
      {...rest}
      render={() => {
        return children;
      }}
    />
  );
};

const DashboardSelector = ({ children, ...rest }) => {
  const hasOrgAccess = useOrganizationPermission();
  const userBranches = useUserBranches();
  let route = "/unauthorized";
  if (hasOrgAccess) {
    route = "/admin";
  } else if (userBranches.length === 1) {
    route = `/branches/${userBranches[0].id}`;
  } else if (userBranches.length > 1) {
    route = "/branchselect";
  }
  return (
    <Route
      {...rest}
      render={({ location }) => {
        return (
          <Redirect
            to={{
              pathname: route,
              state: { from: location },
            }}
          />
        );
      }}
    />
  );
};

const SecuredRoutes = ({ children }) => {
  const isLoading = useSelector((state) => state.user.loading);
  const profile = useProfile();
  const registrationInProgress = useSelector(
    (state) => state.registration.registrationInProgress
  );
  const user = useSelector((state) => state.user.firebaseUser);

  useEffect(() => {
    if (user) {
      Auth.handleUserSignIn(user);
    }
  }, [user]);

  if (isLoading) {
    return null;
  }

  if (profile == null || registrationInProgress) {
    return <Redirect to="/login" />;
  }

  return (
    <Switch>
      <DashboardSelector exact path={"/"} />
      <RestrictedRoute path="/branchselect">
        <BranchSelector />
      </RestrictedRoute>
      <RestrictedRoute path="/admin">
        <AdminDashboard />
      </RestrictedRoute>
      <RestrictedRoute path="/branches/:branchId">
        <BranchDashboard />
      </RestrictedRoute>
    </Switch>
  );
};

const AppRoutes = ({ location }) => {
  return (
    <Router>
      <Switch>
        <Route path={"/unauthorized"}>
          <Unauthorized />
        </Route>
        <Route path={"/verify_email"}>
          <Verify />
        </Route>
        <Route exact path={"/change_password"}>
          <ChangePassword />
        </Route>
        <Route exact path={"/link_provider"}>
          <LinkProvider />
        </Route>
        <Route exact path={"/logout"}>
          <SignOut />
        </Route>
        <Route exact path={"/login"}>
          <SignIn />
        </Route>
        <Route exact path={"/forgotpassword"}>
          <Forgot />
        </Route>
        <Route path="/">
          <SecuredRoutes />
        </Route>
      </Switch>
    </Router>
  );
};

export default AppRoutes;
